<template>
	<div ref="root" class="brand-egg-wrap">
		<!--底部发现提示：仅当页面已滚动到底部且彩蛋尚未拉出时出现-->
		<div class="brand-egg-hint" :class="{ 'is-visible': hintVisible }" aria-hidden="true">
			<span class="brand-egg-hint__chevron"></span>
			<span class="brand-egg-hint__text">再往下滑</span>
		</div>
		<!--彩蛋面板：参与文档流，高度由展开进度驱动，升起时把页脚顶上去而不是盖住-->
		<div ref="panel" class="brand-egg">
			<div class="brand-egg__inner">
				<a
					class="brand-egg__link"
					:href="link"
					target="_blank"
					rel="external nofollow noopener"
					:aria-label="text"
					@mouseenter="onHoverEnter"
					@mousemove="onHoverMove"
					@mouseleave="onHoverLeave"
					@touchstart.passive="onHoverEnter"
					@touchmove.passive="onHoverMove"
					@touchend.passive="onHoverLeave"
				>
					<canvas ref="canvas" class="brand-egg__canvas"></canvas>
				</a>
			</div>
		</div>
	</div>
</template>

<script>
	/**
	 * 页脚品牌彩蛋
	 *
	 * <p>平时完全隐藏在视口下方，正常浏览不会看到任何痕迹；
		 * 当页面已经滚动到最底部、用户仍继续向下滑动时，彩蛋会被从底部拉出，
		 * 字母在展开过程中从下方聚合成形。展开后鼠标划过哪个字母，那个字母就会
		 * 以光标为圆心原地破碎变形、整体保持可见，移开后复原；点击后在新窗口打开配置的地址。</p>
	 */

	/** 彩蛋文字 */
	const TEXT = 'TRBOY';

	/** 点击跳转地址 */
	const LINK = 'https://www.trae.cn/pricing';

	/** 绘制字体（优先使用超粗无衬线字体，失败时回退系统字体） */
	const FONT_FAMILY = '"Arial Black", "Helvetica Neue", Impact, "Microsoft YaHei", sans-serif';

	/** 碎屑数量上限，方块尺寸会按字形面积自动推算，保证整字采样不被截断 */
	const MAX_PARTICLES = 6000;

	/** 从底部完全拉出彩蛋所需的额外下滑累计距离（像素） */
	const OPEN_DISTANCE = 520;

	/** 展开/收起的缓动系数，越小越柔和 */
	const OPEN_EASE = 0.18;

	/** 展开到什么程度时让文字碎屑聚合入场（0~1） */
	const OPEN_ASSEMBLE_AT = 0.6;

	/** 变形缓动系数：碎块向目标位置逼近的速度，越小越柔和 */
	const DEFORM_EASE = 0.22;

	/** 变形幅度：碎块偏离原位最多达到自身边长的多少倍（倍数越大破碎感越强） */
	const DEFORM_STRENGTH = 3.2;

	/** 判定「已到达页面底部」的容差（像素） */
	const BOTTOM_TOLERANCE = 8;

	/** 滚动停止后需要静置多久才算「在底部停稳」（毫秒） */
	const REST_DELAY = 220;

	export default {
		name: 'BrandEasterEgg',
		data() {
			return {
				text: TEXT,
				link: LINK,
				hintVisible: false
			};
		},
		mounted() {
			// 非响应式内部状态：碎屑数量较多，不能放进 data 触发深度观测
			this.particles = [];
			this.ctx = null;
			this.layout = null;
			this.width = 0;
			this.height = 0;
			this.dpr = 1;
			// 面板完全升起时占用的高度（由 CSS 决定），脚本只负责读取）
			this.panelHeight = 0;
			// 面板的 CSS 原始高度（由 clamp 决定），展开时按视口与页脚实际高度动态收缩
			this.cssPanelHeight = 0;
			// 是否已在本轮展开中完成高度收缩
			this.capped = false;
			this.glyphTop = 0;
			this.glyphBottom = 0;
			// 每个字母的横向区间，用于判定鼠标划在哪个字母上
			this.letters = [];
			this.ink = '#06161c';
			this.built = false;
			this.rafId = null;
			this.openRafId = null;
			this.resizeTimer = null;
			this.restTimer = null;
			this.touchY = 0;
			this.target = 0;
			this.display = 0;
			// 当前鼠标所在的字母下标，-1 表示不在任何字母上
			this.activeLetter = -1;
			// 画布左/上边界（视口坐标），用于换算指针位置
			this.canvasLeft = 0;
			this.canvasTop = 0;
			// 渲染帧计数，用于碎块颤抖的相位推进
			this.tickFrame = 0;
			// 「已在底部停稳」标志：只有停稳之后再次下滑才会拉出彩蛋，
			// 避免滚到底部的惯性把彩蛋顺带带出来
			this.armed = false;
			this.assembled = false;
			this.reduceMotion = typeof window.matchMedia === 'function' &&
				window.matchMedia('(prefers-reduced-motion: reduce)').matches;

			this.onScroll = () => {
				// 只要还在滚动，就不算「停稳」
				this.deferArm();
				// 离开页面底部即收起，保证彩蛋只在底部被「拉」出来
				if (this.target > 0 && !this.atBottom()) {
					this.setProgress(0);
				}
				this.updateHint();
			};
			this.onWheel = (e) => {
				if (this.reduceMotion) {
					return;
				}
				const unit = e.deltaMode === 1 ? 16 : (e.deltaMode === 2 ? (window.innerHeight || 800) : 1);
				const delta = e.deltaY * unit;
				if (delta === 0) {
					return;
				}
				if (delta < 0) {
					// 向上滚动：任何时候都允许收起
					if (this.target > 0) {
						this.setProgress(this.target + delta / OPEN_DISTANCE);
					}
					return;
				}
				// 已经拉出来的彩蛋继续下滑就继续伸展，不再受「停稳」约束
				if (this.target <= 0 && (!this.armed || !this.atBottom())) {
					this.deferArm();
					return;
				}
				this.setProgress(this.target + delta / OPEN_DISTANCE);
			};
			this.onTouchStart = (e) => {
				this.touchY = e.touches && e.touches.length ? e.touches[0].clientY : 0;
			};
			this.onTouchMove = (e) => {
				if (this.reduceMotion || !e.touches || !e.touches.length) {
					return;
				}
				const currentY = e.touches[0].clientY;
				const delta = this.touchY - currentY;
				this.touchY = currentY;
				if (delta === 0) {
					return;
				}
				if (delta < 0) {
					if (this.target > 0) {
						this.setProgress(this.target + delta / OPEN_DISTANCE);
					}
					return;
				}
				if (this.target <= 0 && (!this.armed || !this.atBottom())) {
					this.deferArm();
					return;
				}
				this.setProgress(this.target + delta / OPEN_DISTANCE);
			};
			this.onResize = () => {
				clearTimeout(this.resizeTimer);
				this.resizeTimer = setTimeout(() => {
					this.rebuild();
					this.applyDisplay();
				}, 200);
			};

			window.addEventListener('scroll', this.onScroll, { passive: true });
			window.addEventListener('wheel', this.onWheel, { passive: true });
			window.addEventListener('touchstart', this.onTouchStart, { passive: true });
			window.addEventListener('touchmove', this.onTouchMove, { passive: true });
			window.addEventListener('resize', this.onResize);

			this.$nextTick(() => {
				this.rebuild();
				this.applyDisplay();
			});
		},
		beforeDestroy() {
			window.removeEventListener('scroll', this.onScroll);
			window.removeEventListener('wheel', this.onWheel);
			window.removeEventListener('touchstart', this.onTouchStart);
			window.removeEventListener('touchmove', this.onTouchMove);
			window.removeEventListener('resize', this.onResize);
			clearTimeout(this.resizeTimer);
			clearTimeout(this.restTimer);
			this.restTimer = null;
			if (this.rafId !== null) {
				cancelAnimationFrame(this.rafId);
				this.rafId = null;
			}
			if (this.openRafId !== null) {
				cancelAnimationFrame(this.openRafId);
				this.openRafId = null;
			}
			this.particles = [];
		},
		methods: {
			/**
			 * 获取文档总高度
			 *
			 * @return 文档总高度（像素）
			 */
			docHeight() {
				const bodyHeight = document.body ? document.body.scrollHeight : 0;
				const docHeight = document.documentElement ? document.documentElement.scrollHeight : 0;
				return Math.max(bodyHeight, docHeight, window.innerHeight || 0);
			},

			/**
			 * 判断页面是否已滚动到底部
			 *
			 * @return 是否已在底部
			 */
			atBottom() {
				const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
				const scrolled = window.pageYOffset || document.documentElement.scrollTop || 0;
				return scrolled + viewportHeight >= this.docHeight() - BOTTOM_TOLERANCE;
			},

			/**
			 * 推迟「停稳」判定
			 *
			 * <p>每次滚动或下滑都重新计时，只有连续 REST_DELAY 毫秒没有任何滚动动作，
			 * 且此刻确实位于页面底部，才认为用户已经停稳，允许拉出彩蛋。</p>
			 */
			deferArm() {
				this.armed = false;
				clearTimeout(this.restTimer);
				this.restTimer = setTimeout(() => {
					this.restTimer = null;
					this.armed = this.atBottom();
				}, REST_DELAY);
			},

			/**
			 * 按视口与版权页脚的实际高度收缩面板高度
			 *
			 * <p>彩蛋位于页面最底部（页脚之后），展开时页面会变高；如果不做收缩，
			 * 版权页脚会被顶出视口。这里量出版权页脚的真实高度，让「页脚高度 + 彩蛋高度」
			 * 不超过视口高度，从而页脚始终留在视口内、版权信息可见。</p>
			 *
			 * @param cssHeight 面板由样式表决定的原始高度（像素）
			 * @return 收缩后的面板高度（像素）
			 */
			capPanelHeight(cssHeight) {
				const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
				const panel = this.$refs.panel;
				const inner = panel ? panel.querySelector('.brand-egg__inner') : null;
				const footer = panel && panel.parentElement ? panel.parentElement.querySelector('footer') : null;
				const footerHeight = footer ? footer.offsetHeight : 0;
				const available = Math.max(0, viewportHeight - footerHeight);
				const capped = Math.max(60, Math.min(cssHeight, available));
				if (inner) {
					inner.style.height = `${capped}px`;
				}
				return capped;
			},

			/**
			 * 设置彩蛋展开目标进度
			 *
			 * @param value 进度值（会被限制到 0~1）
			 */
			setProgress(value) {
				const next = Math.max(0, Math.min(1, value));
				if (Math.abs(next - this.target) < 0.0005) {
					return;
				}
				this.target = next;
				if (next === 0) {
					// 收起后允许下次重新播放聚合动画，同时把碎屑复位
					this.assembled = false;
					this.activeLetter = -1;
					this.restoreAll();
					// 下一轮展开时重新按当时视口与页脚高度收缩面板
					this.capped = false;
				} else if (!this.capped) {
					// 首次展开时按视口与版权页脚的实际高度收缩面板，保证版权信息不被顶出视口
					this.capped = true;
					this.panelHeight = this.capPanelHeight(this.cssPanelHeight);
				}
				this.startOpenAnimation();
			},

			/**
			 * 启动展开/收起的缓动动画
			 */
			startOpenAnimation() {
				if (this.openRafId !== null) {
					return;
				}
				this.openRafId = requestAnimationFrame(() => {
					this.openRafId = null;
					const diff = this.target - this.display;
					if (Math.abs(diff) < 0.002) {
						this.display = this.target;
						this.applyDisplay();
						return;
					}
					this.display += diff * OPEN_EASE;
					this.applyDisplay();
					this.startOpenAnimation();
				});
			},

			/**
			 * 将当前展开进度应用到视图
			 *
			 * <p>直接写样式而不是走响应式数据，避免每帧触发组件重渲染。</p>
			 */
			applyDisplay() {
				const panel = this.$refs.panel;
				if (!panel) {
					return;
				}
				panel.style.height = `${(this.display * this.panelHeight).toFixed(2)}px`;
				if (this.display > 0) {
					this.pinBottom();
				}
				if (this.display >= OPEN_ASSEMBLE_AT) {
					this.onOpened();
				}
				this.updateHint();
			},

			/**
			 * 把页面滚动位置吸附到最底部
			 *
			 * <p>面板参与文档流，展开时文档会变高；只有同时贴住底部，
			 * 才会呈现「页脚被顶上去、彩蛋从底边升起来」的效果，
			 * 而不是把页脚盖住。</p>
			 */
			pinBottom() {
				const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
				const maxScroll = Math.max(0, this.docHeight() - viewportHeight);
				const current = window.pageYOffset || document.documentElement.scrollTop || 0;
				if (Math.abs(current - maxScroll) > 1) {
					window.scrollTo(0, maxScroll);
				}
			},

			/**
			 * 刷新底部发现提示的显隐
			 *
			 * <p>只在「已滚到最底部、且彩蛋还完全没被拉出」时出现，
			 * 作为刻意探索的线索；一旦开始拉出或离开底部就隐藏。</p>
			 */
			updateHint() {
				const visible = this.target === 0 && this.display < 0.01 && this.atBottom();
				if (visible !== this.hintVisible) {
					this.hintVisible = visible;
				}
			},

			/**
			 * 彩蛋展开到一定程度时的处理
			 *
			 * <p>让文字碎屑从下方聚合成形，与面板上升过程重叠，形成「浮现」的入场观感；
			 * 碎块向原位缓动，到位后由完整字形呈现。</p>
			 */
			onOpened() {
				if (!this.built) {
					this.rebuild();
				}
				if (this.assembled || this.reduceMotion || !this.particles.length) {
					this.assembled = true;
					return;
				}
				this.assembled = true;
				const particles = this.particles;
				for (let i = 0; i < particles.length; i++) {
					const p = particles[i];
					p.x = p.hx;
					p.y = p.hy + this.height * (0.9 + Math.random() * 0.9);
					p.targetX = p.hx;
					p.targetY = p.hy;
					p.state = 1;
				}
				this.scheduleTick();
			},

			/**
			 * 构建画布与碎屑
			 *
			 * <p>按容器实际尺寸重算画布分辨率，将彩蛋文字横向拉伸铺满可用宽度，
			 * 再把文字顶部区域按网格采样成方块碎屑。</p>
			 */
			rebuild() {
				const canvas = this.$refs.canvas;
				if (!canvas) {
					return;
				}
				// 先还原由 CSS clamp 决定的内层高度，避免上一轮按小屏收缩的内联高度残留
				const inner = canvas.closest ? canvas.closest('.brand-egg__inner') : null;
				if (inner) {
					inner.style.height = '';
				}
				const rect = canvas.getBoundingClientRect();
				const width = Math.max(1, Math.round(rect.width));
				const height = Math.max(1, Math.round(rect.height));
				// 面板的 CSS 原始高度；实际展开高度按视口与页脚高度动态收缩（见 capPanelHeight）
				this.cssPanelHeight = height;
				this.panelHeight = this.capPanelHeight(height);
				// 命中判定用的画布左边界，随重建一起刷新
				this.canvasLeft = rect.left;
				const dpr = Math.min(window.devicePixelRatio || 1, 2);

				canvas.width = Math.round(width * dpr);
				canvas.height = Math.round(height * dpr);
				const ctx = canvas.getContext('2d');
				if (!ctx) {
					return;
				}
				ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

				this.ctx = ctx;
				this.width = width;
				this.height = height;
				this.dpr = dpr || 1;
				this.ink = this.resolveInk(canvas);

				if (!this.createLayout(ctx, width, height)) {
					return;
				}
				this.createParticles(ctx, dpr);
				this.built = true;
				this.activeLetter = -1;
				this.drawText(ctx);
			},

			/**
			 * 解析文字颜色
			 *
			 * <p>颜色由样式表统一维护，这里通过计算样式读取，避免样式与脚本两处定义。</p>
			 *
			 * @param canvas 画布元素
			 * @return 文字颜色
			 */
			resolveInk(canvas) {
				const ink = window.getComputedStyle(canvas).color;
				return ink || '#06161c';
			},

			/**
			 * 计算文字排版参数
			 *
			 * <p>先按参考字号测出「TRBOY」的原始宽高比，再等比缩放到刚好铺满可用宽度，
			 * 全程不做横向拉伸，保证字形比例与品牌字一致；最后得到文字外框与碎裂带的下边界。</p>
			 *
			 * @param ctx 画布上下文
			 * @param width 画布宽度（CSS 像素）
			 * @param height 画布高度（CSS 像素）
			 * @return 是否排版成功
			 */
			createLayout(ctx, width, height) {
				const padX = width * 0.012;
				const targetWidth = Math.max(1, width - padX * 2);
				const targetHeight = height * 0.88;
				// 参考字号固定为 100px，用它换算出字形的原始宽高比
				ctx.font = `900 100px ${FONT_FAMILY}`;
				const reference = ctx.measureText(TEXT);
				const referenceWidth = reference.width || 100;
				const referenceHeight = (reference.actualBoundingBoxAscent || 72) +
					(reference.actualBoundingBoxDescent || 0);
				if (!referenceWidth || !referenceHeight) {
					return false;
				}
				const fontSize = Math.min(targetWidth / referenceWidth, targetHeight / referenceHeight) * 100;
				if (!isFinite(fontSize) || fontSize <= 0) {
					return false;
				}
				ctx.font = `900 ${fontSize}px ${FONT_FAMILY}`;
				ctx.textBaseline = 'alphabetic';
				const metrics = ctx.measureText(TEXT);
				const ascent = metrics.actualBoundingBoxAscent || fontSize * 0.75;
				const descent = metrics.actualBoundingBoxDescent || fontSize * 0.02;
				const glyphWidth = metrics.width || 1;
				const glyphHeight = ascent + descent;
				// 居中摆放：左右留白由实际字宽决定，而不是靠横向拉伸去填满
				const left = (width - glyphWidth) / 2;
				const baseY = (height - glyphHeight) / 2 + ascent;
				const top = baseY - ascent;
				const bottom = baseY + descent;

				this.layout = {
					padX: left,
					baseY: baseY,
					fontSize: fontSize,
					scaleX: 1,
					left: left,
					right: left + glyphWidth
				};
				this.glyphTop = top;
				this.glyphBottom = bottom;
				// 逐字母测量横向区间（用前缀宽度累加，兼顾字距），供鼠标划过的命中判定
				const letters = [];
				let prefix = 0;
				for (let i = 0; i < TEXT.length; i++) {
					const next = ctx.measureText(TEXT.slice(0, i + 1)).width;
					letters.push({ left: left + prefix, right: left + next });
					prefix = next;
				}
				this.letters = letters;
				return true;
			},

			/**
			 * 采样生成碎屑
			 *
			 * <p>先绘制一遍完整文字，再按网格读取透明度：命中文字的格子生成一个方块碎屑。
			 * 采样整个字形而不是只采样顶部，这样每个字母都是一个可以整体变形的完整单位；
			 * 方块尺寸按字形面积与碎屑上限反推，保证采样不会被上限截断。</p>
			 *
			 * @param ctx 画布上下文
			 * @param dpr 设备像素比
			 */
			createParticles(ctx, dpr) {
				const canvas = ctx.canvas;
				ctx.clearRect(0, 0, this.width, this.height);
				this.drawText(ctx);

				const left = this.layout.left;
				const right = this.layout.right;
				const top = this.glyphTop;
				const bottom = this.glyphBottom;
				const spanX = Math.max(1, right - left);
				const spanY = Math.max(1, bottom - top);
				const block = Math.max(4, Math.ceil(Math.sqrt(spanX * spanY / MAX_PARTICLES)));
				const half = block / 2;
				const columnCount = Math.max(1, Math.ceil(spanX / block));
				const rowCount = Math.max(1, Math.ceil(spanY / block));

				const dataHeight = Math.max(1, Math.min(canvas.height, Math.ceil(bottom * dpr)));
				const image = ctx.getImageData(0, 0, canvas.width, dataHeight);
				const data = image.data;
				const imageWidth = image.width;

				// 格子内多点采样：命中两点以上视为覆盖文字，避免字形出现空洞
				const samples = [0.5, 0.18, 0.82];

				const particles = [];

				for (let row = 0; row < rowCount; row++) {
					const cy = top + row * block;
					for (let col = 0; col < columnCount; col++) {
						const cx = left + col * block;
						let hit = 0;
						for (let ry = 0; ry < samples.length && hit < 2; ry++) {
							for (let rx = 0; rx < samples.length; rx++) {
								const sx = Math.round((cx + block * samples[rx]) * dpr);
								const sy = Math.round((cy + block * samples[ry]) * dpr);
								if (sx < 0 || sy < 0 || sx >= imageWidth || sy >= dataHeight) {
									continue;
								}
								if (data[(sy * imageWidth + sx) * 4 + 3] > 120) {
									hit++;
									break;
								}
							}
						}
						if (hit >= 2) {
							particles.push({
								hx: cx,
								hy: cy,
								x: cx,
								y: cy,
								size: block,
								half: half,
								state: 0,
								letter: this.letterAt(cx + half),
								// 每个碎块固定的随机偏离方向，变形时沿该方向位移
								dirX: (Math.random() - 0.5) * 2,
								dirY: 0.35 + Math.random() * 0.75,
								// 变形目标位置，默认在原位
								targetX: cx,
								targetY: cy,
								// 颤抖相位，让破碎的碎块动起来
								phase: Math.random() * Math.PI * 2
							});
						}
					}
				}
				this.particles = particles;
			},

			/**
			 * 判断某个横坐标落在哪个字母上
			 *
			 * @param x 画布坐标系下的横坐标
			 * @return 字母下标，不在任何字母范围内时返回 -1
			 */
			letterAt(x) {
				const letters = this.letters;
				for (let i = 0; i < letters.length; i++) {
					if (x >= letters[i].left && x < letters[i].right) {
						return i;
					}
				}
				return -1;
			},

			/**
			 * 绘制完整彩蛋文字
			 *
			 * @param ctx 画布上下文
			 */
			drawText(ctx) {
				if (!this.layout) {
					return;
				}
				ctx.save();
				ctx.globalAlpha = 1;
				ctx.translate(this.layout.padX, this.layout.baseY);
				ctx.scale(this.layout.scaleX, 1);
				ctx.font = `900 ${this.layout.fontSize}px ${FONT_FAMILY}`;
				ctx.textBaseline = 'alphabetic';
				ctx.fillStyle = this.ink;
				ctx.fillText(TEXT, 0, 0);
				ctx.restore();
			},

			/**
			 * 取事件里的横坐标（兼容鼠标与触摸）
			 *
			 * @param e 事件对象
			 * @return 视口横坐标
			 */
			pointerX(e) {
				return e.touches && e.touches.length ? e.touches[0].clientX : e.clientX;
			},

			/**
			 * 取事件里的纵坐标（兼容鼠标与触摸）
			 *
			 * @param e 事件对象
			 * @return 视口纵坐标
			 */
			pointerY(e) {
				return e.touches && e.touches.length ? e.touches[0].clientY : e.clientY;
			},

			/**
			 * 指针进入画布
			 *
			 * @param e 事件对象
			 */
			onHoverEnter(e) {
				const canvas = this.$refs.canvas;
				if (canvas) {
					const rect = canvas.getBoundingClientRect();
					this.canvasLeft = rect.left;
					this.canvasTop = rect.top;
				}
				this.onHoverMove(e);
			},

			/**
			 * 指针在画布上移动
			 *
			 * <p>光标落在哪个字母上，就以光标为圆心让该字母的碎块按距离衰减地
			 * 偏离原位，形成「光标划到哪、哪里的字体范围就变形」的效果；
			 * 碎块始终不脱离原位太远，字母不会消失。离开的字母立刻复位。</p>
			 *
			 * @param e 事件对象
			 */
			onHoverMove(e) {
				if (this.reduceMotion || !this.built || !this.letters.length) {
					return;
				}
				const cursorX = this.pointerX(e) - this.canvasLeft;
				const cursorY = this.pointerY(e) - this.canvasTop;
				const index = this.letterAt(cursorX);
				if (index !== this.activeLetter) {
					const previous = this.activeLetter;
					this.activeLetter = index;
					if (previous >= 0 && previous !== index) {
						this.restoreLetter(previous);
					}
				}
				if (index >= 0) {
					this.deformLetter(index, cursorX, cursorY);
				}
			},

			/**
			 * 指针离开画布：所有字母复位成完整字形
			 */
			onHoverLeave() {
				this.activeLetter = -1;
				this.restoreAll();
			},

			/**
			 * 让某个字母的碎块以光标为圆心变形
			 *
			 * <p>碎块离光标越近偏离越大，随距离衰减到零；每个碎块沿自己随机的
			 * 方向偏离，幅度不超过边长的一定倍数，因此字母整体仍清晰可辨，
			 * 只是轮廓像被砸碎一样错位。光标在字母内移动时，变形区域跟着走。</p>
			 *
			 * @param index 字母下标
			 * @param cursorX 光标在画布内的横坐标
			 * @param cursorY 光标在画布内的纵坐标
			 */
			deformLetter(index, cursorX, cursorY) {
				if (!this.built) {
					this.rebuild();
				}
				if (!this.particles.length || index < 0) {
					return;
				}
				const letter = this.letters[index];
				const radius = Math.max(30, (letter.right - letter.left) * 0.75);
				const particles = this.particles;
				for (let i = 0; i < particles.length; i++) {
					const p = particles[i];
					if (p.letter !== index) {
						continue;
					}
					const d = Math.hypot(p.hx + p.half - cursorX, p.hy + p.half - cursorY);
					const m = Math.max(0, 1 - d / radius);
					p.targetX = p.hx + p.dirX * m * DEFORM_STRENGTH * p.size;
					p.targetY = p.hy + p.dirY * m * DEFORM_STRENGTH * p.size;
					p.state = m > 0 ? 1 : 0;
				}
				this.scheduleTick();
			},

			/**
			 * 让某个字母的碎块全部复位
			 *
			 * @param index 字母下标，-1 表示全部
			 */
			restoreLetter(index) {
				const particles = this.particles;
				let animating = false;
				for (let i = 0; i < particles.length; i++) {
					const p = particles[i];
					if (index >= 0 && p.letter !== index) {
						continue;
					}
					if (p.targetX === p.hx && p.targetY === p.hy && p.state === 0) {
						continue;
					}
					p.targetX = p.hx;
					p.targetY = p.hy;
					p.state = 1;
					animating = true;
				}
				if (animating) {
					this.scheduleTick();
				} else if (index < 0) {
					this.redraw();
				}
			},

			/**
			 * 复位全部字母
			 */
			restoreAll() {
				this.restoreLetter(-1);
			},

			/**
			 * 安排下一帧动画
			 */
			scheduleTick() {
				if (this.rafId !== null) {
					return;
				}
				this.rafId = requestAnimationFrame(() => {
					this.rafId = null;
					this.render(true);
				});
			},

			/**
			 * 立即重绘一帧（不推进物理）
			 */
			redraw() {
				if (this.ctx) {
					this.render(false);
				}
			},

			/**
			 * 渲染一帧
			 *
			 * <p>先绘制完整字形作为底色，再把偏离原位的碎块画在对应位置；
			 * 碎块移走留下的缺口就是「破碎」的观感，字母整体始终完整可见、不会消失。</p>
			 *
			 * @param advance 是否推进物理计算
			 * @return 是否仍有动画在继续
			 */
			render(advance) {
				const ctx = this.ctx;
				if (!ctx || !this.layout) {
					return false;
				}
				const particles = this.particles;
				let busy = false;

				if (advance) {
					this.tickFrame++;
					for (let i = 0; i < particles.length; i++) {
						const p = particles[i];
						const dx = p.targetX - p.x;
						const dy = p.targetY - p.y;
						const dist = Math.sqrt(dx * dx + dy * dy);
						if (dist < 0.25) {
							p.x = p.targetX;
							p.y = p.targetY;
							if (p.targetX === p.hx && p.targetY === p.hy) {
								p.state = 0;
							} else {
								p.state = 1;
							}
						} else {
							p.x += dx * DEFORM_EASE;
							p.y += dy * DEFORM_EASE;
							p.state = 1;
							busy = true;
						}
					}
				}

				ctx.globalAlpha = 1;
				ctx.clearRect(0, 0, this.width, this.height);
				this.drawText(ctx);

				const dpr = this.dpr;
				// 悬停变形时，先把偏离原位的碎块在原位置挖出缺口，
				// 让「破碎」不只是边缘突出，而是字形上出现真实的裂口；
				// 组装入场等非悬停场景不挖洞，避免字母出现大片空洞。
				if (this.activeLetter >= 0) {
					ctx.save();
					ctx.globalCompositeOperation = 'destination-out';
					ctx.fillStyle = '#000';
					for (let i = 0; i < particles.length; i++) {
						const p = particles[i];
						if (p.state === 0) {
							continue;
						}
						// 只挖离原位不远的碎块（悬停变形的量级），组装入场时远距离碎块不挖
						const away = Math.hypot(p.x - p.hx, p.y - p.hy);
						if (away > p.size * 4.5) {
							continue;
						}
						const x0 = Math.round(p.hx * dpr) / dpr;
						const y0 = Math.round(p.hy * dpr) / dpr;
						const x1 = Math.round((p.hx + p.size) * dpr) / dpr;
						const y1 = Math.round((p.hy + p.size) * dpr) / dpr;
						ctx.fillRect(x0, y0, x1 - x0, y1 - y0);
					}
					ctx.restore();
				}

				// 把偏离原位的碎块叠画在字形上，轻微颤抖让「破碎」更生动
				ctx.fillStyle = this.ink;
				const wobble = this.tickFrame * 0.07;
				for (let i = 0; i < particles.length; i++) {
					const p = particles[i];
					if (p.state === 0) {
						continue;
					}
					const wx = Math.sin(wobble + p.phase) * 1.8;
					const wy = Math.cos(wobble * 1.3 + p.phase) * 1.8;
					const x0 = Math.round((p.x + wx) * dpr) / dpr;
					const y0 = Math.round((p.y + wy) * dpr) / dpr;
					const x1 = Math.round((p.x + p.size + wx) * dpr) / dpr;
					const y1 = Math.round((p.y + p.size + wy) * dpr) / dpr;
					ctx.fillRect(x0, y0, x1 - x0, y1 - y0);
				}

				if (advance && busy) {
					this.scheduleTick();
				}
				return busy;
			}
		}
	};
</script>

<style scoped>
	.brand-egg-wrap {
		position: relative;
		width: 100%;
	}

	.brand-egg-hint {
		position: fixed;
		left: 50%;
		bottom: 12px;
		z-index: 901;
		display: flex;
		flex-direction: column;
		align-items: center;
		color: #48dbfb;
		font-size: 12px;
		letter-spacing: 1px;
		text-shadow: 0 1px 3px rgba(0, 0, 0, 0.45);
		opacity: 0;
		transform: translate(-50%, 10px);
		transition: opacity 0.35s ease, transform 0.35s ease;
		pointer-events: none;
	}

	.brand-egg-hint.is-visible {
		opacity: 1;
		transform: translate(-50%, 0);
	}

	.brand-egg-hint__chevron {
		width: 10px;
		height: 10px;
		margin-bottom: 4px;
		border-right: 2px solid currentColor;
		border-bottom: 2px solid currentColor;
		animation: brand-egg-bounce 1.4s ease-in-out infinite;
	}

	@keyframes brand-egg-bounce {
		0%, 100% {
			transform: rotate(45deg) translate(0, 0);
		}
		50% {
			transform: rotate(45deg) translate(3px, 3px);
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.brand-egg-hint__chevron {
			animation: none;
			transform: rotate(45deg);
		}
	}

	.brand-egg {
		--egg-bg: #48dbfb;
		--egg-ink: #06161c;
		position: relative;
		width: 100%;
		height: 0;
		overflow: hidden;
		-webkit-tap-highlight-color: transparent;
	}

	.brand-egg__inner {
			position: absolute;
			top: 0;
			right: 0;
			left: 0;
			height: clamp(120px, 25vw, 440px);
			background: var(--egg-bg);
		}

	.brand-egg__link {
		display: block;
		width: 100%;
		max-width: 1600px;
		height: 100%;
		margin: 0 auto;
		outline: none;
		cursor: pointer;
	}

	.brand-egg__canvas {
		display: block;
		width: 100%;
		height: 100%;
		color: var(--egg-ink);
	}
</style>
